import React, {useEffect, useState} from 'react';
import {
  FieldErrors,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import {Box, Heading} from 'native-base';
import {StyleSheet} from 'react-native';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {StackParamList} from '../../../../navigators';
import InternalTemplate from '../../../../components/templates/internal';
import {PrimaryButton, SecondaryButton} from '../../../../components/buttons';
import useToast from '../../../../hooks';
import {useSalesmanContext} from '../../../../context/salesman';
import {DropDownInput} from '../../../../components/inputs';
import IDropDownOption from '../../../../interfaces/ui/dropdown-option.interface';
import {message} from '../../../../utils/messages';
import {IClient} from '../../../../interfaces/dtos';
import {OrderDepartament, OrderStatus} from '../../../../interfaces/types';
import {SearchInputLauncher} from '../../../../components/inputs/search/launcher/SearchInputLauncher';

type ScreenProps = NativeStackScreenProps<
  StackParamList,
  'SalesmanOrderListFilter'
>;

const OrderListFilterScreen = ({navigation}: ScreenProps) => {
  const {showInputsError} = useToast();

  const {
    salesmanOrderFilter: filter,
    setSalesmanOrderFilter: setFilter,
    client: salesmanClient,
    setSalesmanClient,
  } = useSalesmanContext();

  const [client, setClient] = useState(undefined as IClient | undefined);

  const getDefaultValues = () => {
    return {
      status: filter ? filter.status : '',
      departament: filter ? filter.departament : '',
    } as any;
  };

  const {control, handleSubmit, reset, watch} = useForm({
    defaultValues: getDefaultValues(),
  });

  useEffect(() => {
    setClient(salesmanClient);
  }, [salesmanClient]);

  const status = watch('status');
  const departament = watch('departament');

  const handleClearForm = () => {
    setClient(undefined);
    reset({
      status: '',
      departament: '',
    });
  };

  const onInvalid: SubmitErrorHandler<any> = async (
    fieldErrors: FieldErrors<any>,
  ) => {
    if (fieldErrors) {
      showInputsError();
    }
  };

  const onSubmit: SubmitHandler<any> = async (data: any) => {
    let filterObj: any = null;

    for (const property in data) {
      if (data[property] !== '') {
        if (filterObj === null) {
          filterObj = {};
        }
        filterObj[property] = data[property];
      }
    }
    if (filterObj || client) {
      setFilter({
        ...filterObj,
        clientId: client?.id,
      });
    } else {
      setSalesmanClient(null);
      setFilter(null);
    }

    goBack();
  };

  const goBack = () => {
    navigation.navigate('SalesmanOrderList');
  };

  const getListDepartamentOption = (): IDropDownOption[] => {
    const list: IDropDownOption[] = [];
    list.push({
      label: message('administration-list-option'),
      value: 'administration' as OrderDepartament,
    });
    list.push({
      label: message('logistic-list-option'),
      value: 'logistic' as OrderDepartament,
    });
    list.push({
      label: message('production-list-option'),
      value: 'production' as OrderDepartament,
    });
    return list;
  };

  const getListStatusOption = (): IDropDownOption[] => {
    const list: IDropDownOption[] = [];
    list.push({
      label: message('approved-option-list'),
      value: 'approved' as OrderStatus,
    });
    list.push({
      label: message('waiting-option-list'),
      value: 'waiting' as OrderStatus,
    });
    list.push({
      label: message('rejected-option-list'),
      value: 'rejected' as OrderStatus,
    });
    return list;
  };

  const showClearButton = () => {
    return !(status === '' && departament === '' && client === null);
  };

  const getClientName = () => {
    if (client) {
      return client.name;
    }
    return '';
  };

  return (
    <InternalTemplate
      title={'screen-filter-screen-title'}
      contentPaddingX="3"
      childContainerStyle={styles.childContainerStyle}
      backButton={{
        onPress: () => goBack(),
        label: 'back-resume',
        usage: 'external',
      }}>
      <Box marginBottom={20}>
        <Heading size={'sm'} color={'#06355E'} marginBottom={5} marginTop={2}>
          {message('salesman-portfolio-filter-heading')}
        </Heading>
        <>
          <SearchInputLauncher
            label="salesman-order-client-filter-list"
            defaultValue={getClientName()}
            navigateTo={'SalesmanClientList'}
          />
          <DropDownInput
            control={control}
            name="status"
            label="salesman-order-status-filter-list"
            options={getListStatusOption()}
          />

          <DropDownInput
            control={control}
            name="departament"
            label="salesman-order-departament-filter-list"
            options={getListDepartamentOption()}
          />

          <Box mt={'5'}>
            <PrimaryButton
              label="salesman-portfolio-filter-save-button"
              onPress={handleSubmit(onSubmit, onInvalid)}
            />
          </Box>
          {showClearButton() && (
            <Box mt={5}>
              <SecondaryButton
                label="salesman-portfolio-filter-clear-button"
                onPress={handleClearForm}
              />
            </Box>
          )}
        </>
      </Box>
    </InternalTemplate>
  );
};

export default OrderListFilterScreen;

const styles = StyleSheet.create({
  childContainerStyle: {
    paddingBottom: '100%',
  },
});
